import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/helpers';
import type { AdminProductRow } from '../productManagement';
import AdminProductMobileList from './AdminProductMobileList';
import ProductImage from './ProductImage';
import ProductStockBadge from './ProductStockBadge';

interface ProductTableProps {
  rows: AdminProductRow[];
  currentPage: number;
  totalPages: number;
  totalElements: number;
  rangeStart: number;
  rangeEnd: number;
  isLoading: boolean;
  isInitialLoading: boolean;
  error: string;
  hasActiveFilters: boolean;
  onPageChange: (page: number) => void;
  onRetry: () => void;
  onClearFilters: () => void;
}

const ProductTable: React.FC<ProductTableProps> = ({
  rows,
  currentPage,
  totalPages,
  totalElements,
  rangeStart,
  rangeEnd,
  isLoading,
  isInitialLoading,
  error,
  hasActiveFilters,
  onPageChange,
  onRetry,
  onClearFilters,
}) => {
  const { t } = useTranslation();

  const renderTableState = () => {
    if (isInitialLoading) {
      return Array.from({ length: 6 }, (_, index) => (
        <tr key={index}>
          <td colSpan={9} className="px-5 py-3">
            <div className="h-11 animate-pulse rounded-lg bg-surface-container-low" />
          </td>
        </tr>
      ));
    }

    if (error && rows.length === 0) {
      return (
        <tr>
          <td colSpan={9} className="px-5 py-14 text-center">
            <span className="material-symbols-outlined text-4xl text-error">cloud_off</span>
            <p className="mt-2 font-medium text-on-surface">{t('admin.products.error.title')}</p>
            <p className="mt-1 text-body-sm text-error">{error}</p>
            <button type="button" className="mt-3 rounded-lg bg-primary px-4 py-2 font-label-md text-on-primary" onClick={onRetry}>
              {t('admin.products.actions.retry')}
            </button>
          </td>
        </tr>
      );
    }

    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={9} className="px-5 py-14 text-center text-secondary">
            <span className="material-symbols-outlined text-4xl text-outline">inventory_2</span>
            <p className="mt-2 font-medium text-on-surface">
              {t(hasActiveFilters ? 'admin.products.empty.filtered_title' : 'admin.products.empty.title')}
            </p>
            <p className="mt-1">
              {t(hasActiveFilters ? 'admin.products.empty.filtered_description' : 'admin.products.empty.description')}
            </p>
            {hasActiveFilters && (
              <button type="button" className="mt-3 rounded-lg border border-outline-variant px-4 py-2 font-label-md text-primary hover:bg-primary-fixed/40" onClick={onClearFilters}>
                {t('admin.products.actions.clear_filters')}
              </button>
            )}
          </td>
        </tr>
      );
    }

    return rows.map(({ product, variant, cost, quantity, status }) => (
      <tr key={`${product.id}-${variant.id}`} className="group hover:bg-surface-container-lowest">
        <td className="px-5 py-3"><input type="checkbox" aria-label={t('admin.products.table.select_product', { name: product.name })} /></td>
        <td className="px-5 py-3">
          <div className="flex items-center gap-3">
            <ProductImage name={product.name} className="h-11 w-11 rounded-xl" />
            <div className="min-w-0">
              <p className="max-w-[280px] truncate font-medium text-on-surface">{product.name}</p>
              <p className="max-w-[280px] truncate text-[12px] text-secondary">{variant.versionName} · {variant.color}</p>
            </div>
          </div>
        </td>
        <td className="px-5 py-3 font-mono text-secondary">{variant.id > 0 ? variant.id : t('admin.products.table.automatic')}</td>
        <td className="px-5 py-3 text-secondary">{product.category?.name || '—'}</td>
        <td className="px-5 py-3 text-secondary">{product.brand?.name || '—'}</td>
        <td className="px-5 py-3 text-right font-mono text-secondary">{cost === null ? '—' : formatCurrency(cost)}</td>
        <td className="px-5 py-3 text-right font-mono font-medium text-on-surface">{formatCurrency(variant.price)}</td>
        <td className="px-5 py-3 text-right font-mono font-semibold text-on-surface">{quantity}</td>
        <td className="px-5 py-3 text-center"><ProductStockBadge status={status} /></td>
      </tr>
    ));
  };

  return (
    <div className="relative flex min-h-[360px] flex-1 flex-col">
      {isLoading && !isInitialLoading && (
        <div className="absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-primary-fixed" role="status" aria-label={t('admin.products.loading')}>
          <div className="h-full w-1/2 animate-pulse bg-primary" />
        </div>
      )}

      <div className="flex-1 xl:hidden">
        <AdminProductMobileList rows={rows} isInitialLoading={isInitialLoading} error={error} hasActiveFilters={hasActiveFilters} onRetry={onRetry} onClearFilters={onClearFilters} />
      </div>

      <div className="hidden flex-1 overflow-x-auto xl:block">
        <table className={`w-full min-w-[1080px] border-collapse text-left text-body-sm transition-opacity ${isLoading && !isInitialLoading ? 'opacity-60' : ''}`}>
          <thead className="bg-surface-container-low">
            <tr className="border-b border-outline-variant">
              <th className="w-12 px-5 py-3"><input type="checkbox" aria-label={t('admin.products.table.select_all')} /></th>
              {(['product', 'sku', 'category', 'brand', 'cost', 'price', 'stock', 'status'] as const).map((column) => (
                <th key={column} className={`px-5 py-3 font-label-sm text-label-sm text-secondary ${['cost', 'price', 'stock'].includes(column) ? 'text-right' : column === 'status' ? 'text-center' : ''}`}>
                  {t(`admin.products.table.${column}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/60 bg-white">{renderTableState()}</tbody>
        </table>
      </div>

      <footer className="flex flex-col items-center justify-between gap-3 border-t border-outline-variant bg-white px-4 py-3 text-center sm:flex-row sm:text-left">
        <p className="text-body-sm text-secondary">
          {hasActiveFilters
            ? t('admin.products.pagination.filtered_summary', { count: rows.length })
            : t('admin.products.pagination.summary', { first: rangeStart, last: rangeEnd, total: totalElements })}
        </p>
        <div className="flex items-center gap-2">
          <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant text-secondary disabled:opacity-40" disabled={currentPage <= 1 || isLoading} aria-label={t('admin.products.pagination.previous')} onClick={() => onPageChange(currentPage - 1)}>
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
          <span className="min-w-20 text-center font-label-md text-on-surface">{t('admin.products.pagination.page', { current: currentPage, total: Math.max(totalPages, 1) })}</span>
          <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant text-secondary disabled:opacity-40" disabled={currentPage >= totalPages || isLoading} aria-label={t('admin.products.pagination.next')} onClick={() => onPageChange(currentPage + 1)}>
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      </footer>
    </div>
  );
};

export default ProductTable;
